import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, XCircle, Info, CheckCircle, RefreshCw } from 'lucide-react';
import { systemHealthService } from '../../services/systemHealthService';
import type { SystemEvent, EventSeverity } from '../../services/systemHealthService';

const MODULE_COLORS: Record<string, string> = {
  ai: 'bg-blue-100 text-blue-700',
  notifications: 'bg-violet-100 text-violet-700',
  calendar: 'bg-teal-100 text-teal-700',
  backups: 'bg-orange-100 text-orange-700',
  system: 'bg-gray-100 text-gray-700',
};

const SEVERITY_CONFIG: Record<EventSeverity, { icon: React.ReactNode; label: string; badge: string; row: string }> = {
  info: {
    icon: <Info className="w-3.5 h-3.5" />,
    label: 'Info',
    badge: 'bg-blue-50 text-blue-700 border-blue-100',
    row: '',
  },
  warning: {
    icon: <AlertTriangle className="w-3.5 h-3.5" />,
    label: 'Aviso',
    badge: 'bg-amber-50 text-amber-700 border-amber-100',
    row: '',
  },
  error: {
    icon: <XCircle className="w-3.5 h-3.5" />,
    label: 'Error',
    badge: 'bg-red-50 text-red-700 border-red-100',
    row: 'bg-red-50/40',
  },
  critical: {
    icon: <XCircle className="w-3.5 h-3.5" />,
    label: 'Critico',
    badge: 'bg-red-100 text-red-800 border-red-200 font-bold',
    row: 'bg-red-50',
  },
};

const SEVERITY_FILTERS: Array<{ value: EventSeverity | 'all'; label: string }> = [
  { value: 'all', label: 'Todos' },
  { value: 'critical', label: 'Critico' },
  { value: 'error', label: 'Error' },
  { value: 'warning', label: 'Aviso' },
  { value: 'info', label: 'Info' },
];

function formatRelative(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (mins < 1) return 'Ahora';
  if (mins < 60) return `Hace ${mins}m`;
  if (hours < 24) return `Hace ${hours}h`;
  return `Hace ${days}d`;
}

const RecentSystemEvents: React.FC<{ refreshVersion?: number }> = ({ refreshVersion = 0 }) => {
  const [events, setEvents] = useState<SystemEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [severityFilter, setSeverityFilter] = useState<EventSeverity | 'all'>('all');
  const [error, setError] = useState<string | null>(null);

  const loadEvents = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const filter = severityFilter !== 'all' ? severityFilter : undefined;
      const data = await systemHealthService.getRecentEvents(50, filter);
      setEvents(data);
    } catch (err) {
      setError('No se pudieron cargar los eventos del sistema');
    } finally {
      setIsLoading(false);
    }
  }, [severityFilter]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents, refreshVersion]);

  useEffect(() => {
    const interval = setInterval(loadEvents, 60000);
    return () => clearInterval(interval);
  }, [loadEvents]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="text-base font-semibold text-gray-800">Eventos del Sistema</h2>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
            {SEVERITY_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setSeverityFilter(f.value)}
                className={`text-xs px-2.5 py-1 rounded-md font-medium transition-colors ${
                  severityFilter === f.value
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <button
            onClick={loadEvents}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="divide-y divide-gray-100">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="px-4 py-3 animate-pulse flex gap-3 items-center">
                <div className="h-3 bg-gray-200 rounded w-12" />
                <div className="h-3 bg-gray-200 rounded w-20" />
                <div className="h-3 bg-gray-200 rounded w-16" />
                <div className="h-3 bg-gray-200 rounded flex-1" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="px-4 py-8 text-center text-sm text-red-500">{error}</div>
        ) : events.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">
            <CheckCircle className="w-8 h-8 mx-auto mb-2 text-emerald-400" />
            Sin eventos registrados
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {events.map((event) => {
              const sc = SEVERITY_CONFIG[event.severity] ?? SEVERITY_CONFIG.info;
              const modColor = MODULE_COLORS[event.source_module] ?? MODULE_COLORS.system;
              return (
                <div key={event.id} className={`px-4 py-3 flex items-start gap-3 text-sm ${sc.row}`}>
                  <span className="text-xs text-gray-400 whitespace-nowrap mt-0.5 w-16 flex-shrink-0">
                    {formatRelative(event.created_at)}
                  </span>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${modColor}`}>
                    {event.source_module}
                  </span>
                  <span
                    className={`text-xs font-medium px-2 py-0.5 rounded-full border flex items-center gap-1 flex-shrink-0 ${sc.badge}`}
                  >
                    {sc.icon}
                    {sc.label}
                  </span>
                  <span className="text-gray-700 text-xs leading-relaxed line-clamp-2">
                    {event.message}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RecentSystemEvents;
